import React from 'react';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { DatePicker } from '../../../components/DatePicker';
import type { EmployeeMultiSelectOption } from '../../../components/employees/EmployeeMultiSelect';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import {
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../../settings/utils/settingsSectionStyles';
import {
  sanitizeDecimalInput,
  sanitizeDigitsInput,
  sanitizePlateInput,
  sanitizeVinInput,
} from '../format';

export type TextFieldKind = 'text' | 'year' | 'int' | 'money' | 'liters' | 'vin' | 'plate' | 'last4';

export function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="block">
      <span className={SETTINGS_FIELD_LABEL}>
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </span>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function sanitizeTextField(kind: TextFieldKind, raw: string): string {
  if (kind === 'year') return sanitizeDigitsInput(raw, 4);
  if (kind === 'int') return sanitizeDigitsInput(raw, 8);
  if (kind === 'last4') return sanitizeDigitsInput(raw, 4);
  if (kind === 'money') return sanitizeDecimalInput(raw, 2);
  if (kind === 'liters') return sanitizeDecimalInput(raw, 1);
  if (kind === 'vin') return sanitizeVinInput(raw);
  if (kind === 'plate') return sanitizePlateInput(raw);
  return raw;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  required,
  kind = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  kind?: TextFieldKind;
}) {
  const inputMode =
    kind === 'year' || kind === 'int' || kind === 'last4'
      ? 'numeric'
      : kind === 'money' || kind === 'liters'
        ? 'decimal'
        : undefined;

  return (
    <Field label={label} required={required}>
      <input
        className={SETTINGS_INPUT_CLASS}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete="off"
        onChange={(e) => onChange(sanitizeTextField(kind, e.target.value))}
      />
    </Field>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
}) {
  return (
    <Field label={label} required={required}>
      <AppSelectDropdown
        value={value}
        onChange={onChange}
        options={options}
        triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
        menuZIndex={UI_Z_INDEX.portalDropdownInModal}
      />
    </Field>
  );
}

export function DateField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <Field label={label} required={required}>
      <DatePicker value={value} onChange={onChange} menuZIndex={UI_Z_INDEX.portalDropdownInModal} />
    </Field>
  );
}

export function DriverField({
  employees,
  selectedId,
  onChange,
  disabled,
}: {
  employees: EmployeeMultiSelectOption[];
  selectedId: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <Field label="Fahrer">
      <AppSelectDropdown
        value={selectedId}
        onChange={onChange}
        disabled={disabled}
        searchable
        searchPlaceholder="Mitarbeiter suchen…"
        placeholder="Nicht zugewiesen"
        triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
        menuZIndex={UI_Z_INDEX.portalDropdownInModal}
        options={[
          { value: '', label: 'Nicht zugewiesen' },
          ...employees.map((e) => ({
            value: e.id,
            label: e.name,
          })),
        ]}
      />
    </Field>
  );
}
